import type { InvoiceTemplateData, InvoiceRequestData, InvoicePayment } from './InvoiceDocument';
import { formatPaymentMethodLabel, paymentStatusLabel } from '@/lib/paymentStatus';
import { invoiceText } from '@/lib/invoiceTextFields';

function formatMoney(amount: number, currency: string): string {
  const formatted = amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (currency === 'BDT') return formatted;
  return `${currency} ${formatted}`;
}

function formatDateTime(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function Divider() {
  return <div className="my-2" style={{ borderTop: '1px dashed #000' }} />;
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className={`flex justify-between ${bold ? 'font-bold' : ''}`}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}

export function ReceiptDocument({
  template,
  request,
  payment,
  salesPerson,
}: {
  template: InvoiceTemplateData;
  request: InvoiceRequestData;
  payment?: InvoicePayment;
  salesPerson?: string | null;
}) {
  const t = (key: string, vars?: Record<string, string | number>) => invoiceText(template.text_overrides, key, vars);
  const brandName = template.issuer_name?.trim() || 'The.K Seoul';
  const currency = request.currency || 'BDT';
  const customerName = request.company_name || request.recipient_name || 'Customer';

  return (
    <div
      className="receipt-doc bg-white mx-auto"
      style={{
        width: '100%',
        maxWidth: '320px',
        fontFamily: "'Courier New', Courier, monospace",
        color: '#000',
        fontSize: '12px',
        lineHeight: 1.5,
        padding: '4mm 3mm',
        boxSizing: 'border-box',
      }}
    >
      {/* Header — business name, mother company, address, website, phone,
          then the Mushak-6.3 VAT-form declaration required by NBR on
          Bangladeshi retail receipts. */}
      <div className="text-center">
        <p className="m-0 font-bold" style={{ fontSize: '19px' }}>
          {brandName}
        </p>
        {template.legal_entity_name && (
          <p className="m-0" style={{ fontSize: '10px' }}>
            {t('pos_concern_prefix')} {template.legal_entity_name}
          </p>
        )}
        {(template.office_address || template.issuer_address) && (
          <p className="m-0">{template.office_address || template.issuer_address}</p>
        )}
        {template.issuer_contact && (
          <p className="m-0">
            {t('pos_phone_prefix')} {template.issuer_contact}
          </p>
        )}
      </div>

      <p className="text-center font-bold" style={{ margin: '3px 0 0' }}>
        Mushak-6.3
      </p>

      <Divider />

      {/* Doc meta */}
      <Row label={t('pos_label_receipt_no')} value={request.doc_number || 'DRAFT'} />
      <Row label={t('pos_label_date')} value={formatDateTime(request.issue_date)} />
      <Row label={t('pos_label_customer')} value={customerName} />
      {request.company_contact && <Row label={t('pos_label_customer_phone')} value={request.company_contact} />}
      {salesPerson && <Row label={t('pos_label_sales_person')} value={salesPerson} />}

      <Divider />

      {/* Line items */}
      {request.line_items.length === 0 ? (
        <p className="text-center m-0">{t('pos_empty_line_items')}</p>
      ) : (
        request.line_items.map((item, idx) => (
          <div key={idx} className="mb-1.5">
            <p className="m-0">{item.description}</p>
            <div className="flex justify-between">
              <span>
                {item.quantity} × {formatMoney(item.unit_price, currency)}
              </span>
              <span>{formatMoney(item.amount, currency)}</span>
            </div>
          </div>
        ))
      )}

      <Divider />

      {/* Totals */}
      <Row label={t('pos_label_subtotal')} value={formatMoney(request.subtotal, currency)} />
      {request.shipping_bdt > 0 && <Row label={t('pos_label_delivery_charge')} value={formatMoney(request.shipping_bdt, currency)} />}
      {request.discount_bdt > 0 && <Row label={t('pos_label_discount')} value={`-${formatMoney(request.discount_bdt, currency)}`} />}
      {/* Memo only, not part of the sum above — VAT is already price-
          inclusive in Subtotal, so this never adds to Total. */}
      {request.tax_rate > 0 && (
        <Row
          label={
            template.show_vat_amount === false
              ? t('pos_vat_note_rate_only', { rate: request.tax_rate })
              : t('pos_vat_note_with_amount', { rate: request.tax_rate })
          }
          value={template.show_vat_amount === false ? '' : formatMoney(request.tax_amount, currency)}
        />
      )}
      <Divider />
      <Row label={t('pos_label_total', { currency })} value={formatMoney(request.total_amount, currency)} bold />

      {payment && (
        <>
          <Divider />
          <Row label={t('pos_label_payment_method')} value={payment.methodLabel ?? formatPaymentMethodLabel(payment.method)} />
          <Row label={t('pos_label_payment_status')} value={paymentStatusLabel(payment)} bold />
        </>
      )}

      <Divider />

      <p className="text-center m-0">{t('pos_thank_you', { brand: brandName })}</p>
      {template.issuer_website && (
        <p className="text-center m-0" style={{ marginTop: '2px' }}>
          {t('pos_visit_now')} {template.issuer_website}
        </p>
      )}
    </div>
  );
}
